import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Spin } from "antd";

const isPdfDocumentUrl = (url) => {
  if (!url) return false;
  const path = String(url).split("?")[0].split("#")[0].toLowerCase();
  return path.endsWith(".pdf");
};

const DocumentPreviewModal = ({ preview, onClose }) => {
  const url = preview?.url ?? null;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [useIframeFallback, setUseIframeFallback] = useState(false);

  const isPdf = isPdfDocumentUrl(url);
  const showIframe = isPdf || useIframeFallback;

  useEffect(() => {
    if (!url) return undefined;
    setLoading(true);
    setLoadError(false);
    setUseIframeFallback(isPdf);
    const fallbackTimer = window.setTimeout(() => setLoading(false), 15000);
    return () => window.clearTimeout(fallbackTimer);
  }, [url, isPdf]);

  const handleLoadSuccess = () => {
    setLoading(false);
    setLoadError(false);
  };

  const handleImageError = () => {
    if (!useIframeFallback) {
      setUseIframeFallback(true);
      setLoading(true);
      return;
    }
    setLoading(false);
    setLoadError(true);
  };

  const iframeSrc =
    url && showIframe
      ? `${url}${url.includes("#") ? "&" : "#"}toolbar=1&navpanes=0`
      : null;

  return (
    <Modal
      open={!!url}
      title="View document"
      className="document-preview-modal"
      wrapClassName="document-preview-modal-wrap"
      onCancel={onClose}
      destroyOnClose
      centered
      width={960}
      bodyStyle={{ padding: 0 }}
      footer={null}
    >
      <Spin spinning={loading} tip="Loading document...">
        <div className="document-preview-viewer">
          {loadError ? (
            <div className="document-preview-viewer__message">
              <p>Unable to preview this document here.</p>
            </div>
          ) : showIframe && iframeSrc ? (
            <iframe
              title="Document preview"
              className="document-preview-viewer__iframe"
              src={iframeSrc}
              onLoad={handleLoadSuccess}
            />
          ) : url ? (
            <div className="document-preview-viewer__image-wrap">
              <img
                src={url}
                alt="Document preview"
                className="document-preview-viewer__image"
                onLoad={handleLoadSuccess}
                onError={handleImageError}
              />
            </div>
          ) : null}
        </div>
      </Spin>
    </Modal>
  );
};

DocumentPreviewModal.propTypes = {
  preview: PropTypes.shape({
    url: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

export default DocumentPreviewModal;
